import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import ExteriorHome from "@/assets/Img/exterior-home.jpg";
import BeachAerial from "@/assets/Img/beach-aerial.jpg";
import CallIcon from "@/assets/Icons/call.svg?react";
import BookIcon from "@/assets/Icons/book.svg?react";
import CopyPagesIcon from "@/assets/Icons/copy-pages.svg?react";
import { HomeownersContactCard } from "@/components/HomeownersContactCard";
import { FaqSection } from "@/components/FaqSection";
import { CopyTextToClipboard } from "@/utilities/CopyTextToClipboard";
import { isMobile } from "react-device-detect";
import { useState } from "react";
import { CheckIcon } from "@heroicons/react/16/solid";

const CONTACT_PHONE = "+15203420520";
const SCHEDULER_URL = "https://scheduler.zoom.us/sendola-demo/sendola-demo";

export const HomeownersContact = () => {
  const [t] = useTranslation("homeowners");
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = (text: string) => {
    CopyTextToClipboard(text);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };

  return (
    <>
      <Helmet>
        <title>{t("Contact.SEOTitle")}</title>
        <meta httpEquiv="Content-type" content="text/html; charset=UTF-8" />
      </Helmet>
      <div className="w-full flex flex-col pt-28 lg:pt-32">
        {/* Hero Banner */}
        <div className="w-full h-52 md:h-64 xl:h-72 relative">
          <img
            className="w-full h-full object-cover object-center max-w-screen-2xl absolute right-0 top-0"
            src={ExteriorHome}
            alt=""
          />
          <div className="absolute left-0 top-0 w-full h-full from-30% md:from-50% [@media(min-width:2000px)]:from-70% bg-gradient-to-r from-black to-black/40 flex justify-start">
            <div className="w-full md:w-7/12 flex flex-col items-start md:items-center justify-center px-6 sm:px-12 lg:px-24 xl:px-32">
              <div className="w-full lg:w-2/3">
                <p className="text-white font-playfair font-semibold text-3xl lg:text-4xl sm:min-w-max">
                  {t("Contact.HeadingTitle")}
                </p>
                <p className="text-white/90 font-montserrat font-light text-sm sm:text-lg md:text-xl xl:text-2xl mt-2">
                  {t("Contact.HeadingSubtitle")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Questions Strip */}
        <div className="w-full flex items-center justify-center h-24 px-6 sm:px-12 lg:px-24 bg-white">
          <p className="text-homeowners-green font-montserrat font-light text-sm sm:text-base lg:text-xl max-w-screen-sm md:max-w-screen-md text-center">
            {t("Contact.Questions")}
          </p>
        </div>

        {/* Contact Cards */}
        <div className="w-full flex justify-center py-16 lg:py-20 px-6 sm:px-12 lg:px-24 xl:px-32 bg-homeowners-bg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 justify-items-center w-full max-w-[360px] sm:max-w-[560px]">
            <HomeownersContactCard
              icon={<CallIcon className="w-10 h-10 text-homeowners-green" />}
              title={t("Contact.ContactCards.Card1.Title")}
              text={t("Contact.ContactCards.Card1.Text")}
              buttonText={t("Contact.ContactCards.Card1.Button")}
              buttonIcon={
                !isMobile && !isCopied ? (
                  <CopyPagesIcon className="text-white h-5 w-5 animate-fade-in" />
                ) : !isMobile && isCopied ? (
                  <CheckIcon className="text-white h-5 w-5 animate-fade-in" />
                ) : undefined
              }
              link={isMobile ? `tel:${CONTACT_PHONE}` : undefined}
              onClick={!isMobile ? () => handleCopy(CONTACT_PHONE) : undefined}
            />
            <HomeownersContactCard
              icon={<BookIcon className="w-10 h-10 text-homeowners-green" />}
              title={t("Contact.ContactCards.Card2.Title")}
              text={t("Contact.ContactCards.Card2.Text")}
              buttonText={t("Contact.ContactCards.Card2.Button")}
              link={SCHEDULER_URL}
              target="_blank"
              rel={["noopener"]}
            />
          </div>
        </div>

        {/* Trust Section */}
        <div className="w-full flex flex-col items-center pt-10 pb-20 px-6 sm:px-12 lg:px-24 bg-white">
          <div className="flex flex-col w-full items-center gap-8 sm:gap-10 max-w-screen-xl sm:px-10 pt-10 sm:py-10">
            <div className="flex flex-col gap-4">
              <p className="text-homeowners-headline font-playfair text-3xl md:text-4xl font-semibold text-center px-6 lg:px-10">
                {t("Contact.WeAreHere")}
              </p>
              <p className="text-homeowners-green font-montserrat text-xl md:text-2xl font-light text-center px-6 lg:px-10">
                {t("Contact.Available")}
              </p>
            </div>
            <div className="relative flex items-center justify-center w-full h-52 sm:h-96 rounded-3xl overflow-hidden sm:mt-10">
              <img
                className="absolute w-full h-full top-0 left-0 object-cover object-top"
                src={BeachAerial}
                alt=""
              />
              <div className="absolute top-0 left-0 py-8 px-8 sm:px-14 w-full h-full bg-black/50 flex items-center justify-center">
                <p className="text-white font-montserrat text-lg md:text-2xl lg:text-3xl text-center">
                  {t("Testimonial.Quote")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <FaqSection />
      </div>
    </>
  );
};
